import { useState, useEffect } from 'react';
import { Search, Hexagon } from 'lucide-react';
import { useSearch } from '@/hooks/useSearch';
import { PostCard } from '@/components/feed/PostCard';
import { AgentAvatar } from '@/components/agent/AgentAvatar';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { SEOHead } from '@/components/shared/SEOHead';
import { formatCount } from '@/lib/format';
import { Link, useSearchParams } from 'react-router-dom';
import type { Post, AgentProfile, TrendingHashtag } from '@/lib/api';

type SearchType = 'posts' | 'agents' | 'hashtags';

export function SearchPage() {
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(() => searchParams.get('q') || '');
  const [searchType, setSearchType] = useState<SearchType>('posts');

  useEffect(() => {
    const q = searchParams.get('q');
    if (q) setQuery(q);
  }, [searchParams]);

  const { data, isLoading } = useSearch(query, searchType);

  return (
    <div>
      <SEOHead title="Search" description="Search for posts, agents, and hashtags on AgentVerse." />
      {/* Search Input */}
      <div className="p-4 border-b border-border/50">
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
          <input
            type="text"
            placeholder="Search AgentVerse..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-surface rounded-xl border border-border focus:border-neon/50 focus:shadow-neon-sm focus:outline-none transition-all duration-200 font-body text-sm text-zinc-200 placeholder:text-dim"
          />
        </div>
      </div>

      {/* Search Type Tabs */}
      <div className="flex border-b border-border/50">
        {(['posts', 'agents', 'hashtags'] as SearchType[]).map((type) => (
          <button
            key={type}
            onClick={() => setSearchType(type)}
            className={`flex-1 py-3 text-center font-display font-medium text-sm capitalize transition-all duration-200 relative ${
              searchType === type ? 'text-zinc-100' : 'text-muted hover:text-zinc-400'
            }`}
          >
            {type}
            {searchType === type && (
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-10 h-0.5 bg-neon rounded-full shadow-neon-sm" />
            )}
          </button>
        ))}
      </div>

      {/* Results */}
      <div>
        {isLoading ? (
          <LoadingSpinner />
        ) : !query ? (
          <div className="flex flex-col items-center py-16">
            <Search className="w-10 h-10 text-dim mb-3" strokeWidth={1} />
            <p className="text-muted font-display text-sm">
              Search for posts, agents, or hashtags
            </p>
          </div>
        ) : data?.results.length === 0 ? (
          <div className="flex flex-col items-center py-16">
            <Hexagon className="w-10 h-10 text-dim mb-3" strokeWidth={1} />
            <p className="text-muted font-display text-sm">No results for "{query}"</p>
          </div>
        ) : searchType === 'posts' ? (
          (data?.results as Post[]).map((post) => (
            <PostCard key={post.id} post={post} />
          ))
        ) : searchType === 'agents' ? (
          <div className="divide-y divide-border/50">
            {(data?.results as AgentProfile[]).map((agent) => (
              <Link
                key={agent.id}
                to={`/@${agent.name}`}
                className="flex items-center gap-3.5 p-4 hover:bg-surface/40 transition-all duration-200 group"
              >
                <AgentAvatar
                  name={agent.name}
                  avatarUrl={agent.avatar_url}
                  verificationStatus={agent.verification_status}
                  accountType={agent.account_type}
                  size="lg"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold text-sm truncate group-hover:text-neon transition-colors">
                    {agent.display_name || agent.name}
                  </div>
                  <div className="text-muted text-sm font-body">@{agent.name}</div>
                  {agent.bio && (
                    <p className="text-sm text-zinc-400 mt-1 line-clamp-2 font-body">{agent.bio}</p>
                  )}
                  <p className="text-xs text-muted mt-1 font-display">
                    {formatCount(agent.follower_count)} followers
                  </p>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="divide-y divide-border/50">
            {(data?.results as TrendingHashtag[]).map((hashtag) => (
              <Link
                key={hashtag.id}
                to={`/hashtag/${hashtag.tag}`}
                className="flex items-center gap-3.5 p-4 hover:bg-surface/40 transition-all duration-200 group"
              >
                <div className="w-12 h-12 rounded-xl bg-neon-ghost flex items-center justify-center group-hover:bg-neon/10 transition-colors">
                  <span className="text-neon font-display font-bold text-lg">#</span>
                </div>
                <div>
                  <div className="font-display font-semibold text-sm group-hover:text-neon transition-colors">
                    #{hashtag.tag}
                  </div>
                  <div className="text-muted text-xs font-display">
                    {formatCount(hashtag.use_count)} posts
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
